import { useRef, useState } from "react";
import type { DocumentRecord } from "../lib/db";
import { extractFromFile } from "../lib/extract";

interface Props {
  mode: "paste" | "upload";
  onClose: () => void;
  onSubmit: (text: string, sourceType: DocumentRecord["sourceType"]) => void;
}

export function InputSheet({ mode, onClose, onSubmit }: Props) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePasteFromClipboard = async () => {
    setError(null);
    try {
      const clip = await navigator.clipboard.readText();
      if (!clip.trim()) {
        setError("Clipboard is empty.");
        return;
      }
      setText(clip);
    } catch {
      setError("Couldn't read clipboard. Paste manually below with Ctrl+V / long-press.");
    }
  };

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setProgress("Starting...");
    try {
      const result = await extractFromFile(file, setProgress);
      onSubmit(result.text, result.sourceType);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to read file.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        <h2>{mode === "paste" ? "Paste text" : "Upload a file"}</h2>

        {mode === "upload" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.pdf,.docx,.png,.jpg,.jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <button className="btn btn-primary btn-block" disabled={busy} onClick={() => fileInputRef.current?.click()}>
              {busy ? "Reading file..." : "Choose file"}
            </button>
            <p className="reader-sub">Supports .txt, .pdf, .docx, and photos/screenshots (OCR).</p>
            {progress && <p className="status-line">{progress}</p>}
          </>
        )}

        {mode === "paste" && (
          <>
            <div className="row">
              <button className="btn btn-secondary" onClick={handlePasteFromClipboard}>
                Paste from clipboard
              </button>
            </div>
            <textarea
              className="editor"
              placeholder="Paste or type your text here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
            />
            <button
              className="btn btn-primary btn-block"
              disabled={!text.trim()}
              onClick={() => onSubmit(text, "paste")}
            >
              Use this text
            </button>
          </>
        )}

        {error && <p className="status-line error">{error}</p>}

        <button className="btn btn-secondary btn-block" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
